import { Popover, PopoverProps, message } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { PhrasesTextElement } from 'services/api/phrase';
import { useMemoizedFn, useUnmount, useDebounceEffect } from 'ahooks';
import { css } from '@emotion/css';
import { useSearchPhraseV2 } from 'views/Setting/Phrase/hooks/useSearch';
import PhraseItem, { PhraseItemRef, getPhraseTextElement } from './PhraseItem';
import { PhraseToolbar } from './PhraseToolbar';
import { usePhraseTranslateOpened } from 'hooks/business/usePhraseTranslateOpened';
import { getFillPhraseText, sortPhraseList } from './utils';
import { useTranslate } from '@tolgee/react';
import { useSetConversationToggle } from 'hooks/business/conversationsHooks/useConversationHotKey';
import { PhraseValue } from 'hooks/business/usePhraseOnChat';
import { useKeyboardHandler } from 'hooks/useKeyboardManager';
import { KEYBOARD_PRIORITY } from 'hooks/useKeyboardManager/KeyboardEventManager';

const PopoverContent = styled.div`
  padding-bottom: 6px;
`;
const PopoverContentHeader = styled.div`
  display: flex;
  flex-direction: row-reverse;
  padding: 4px 12px;
`;
const PopoverItems = styled.div`
  max-height: 260px;
  overflow: auto;
`;
const popoverCls = css`
  .ant-popover-inner-content {
    padding: 0;
  }
`;

const countLimit = 20;

export interface PassivePopoverProps extends PopoverProps {
  phraseList?: PhraseValue[];
  // 是否启动联想
  isRelateRegion: boolean;
  region: General.UpperCaseRegion;
  visible?: boolean;
  mercadoQAmode?: boolean;
  conversationToggle?: boolean;
  input: string;
  setInput: (input: string) => void;
  onPhraseSelected: (phraseValue?: PhraseValue) => void;
  onPressEnter: (phraseValue?: PhraseValue) => void;
  onFillPhraseText: (data: string) => void;
  onContentClick?: () => void;
}

function PassivePopover(props: PassivePopoverProps) {
  const { phraseList, isRelateRegion, children, ...otherProps } = props;

  if (!isRelateRegion || !phraseList || phraseList?.length === 0) {
    return <>{children}</>;
  }

  return (
    <Implement phraseList={phraseList} {...otherProps}>
      {children}
    </Implement>
  );
}

interface ImplementProps extends PopoverProps {
  phraseList?: PhraseValue[];
  region: General.UpperCaseRegion;
  visible?: boolean;
  mercadoQAmode?: boolean;
  conversationToggle?: boolean;
  input: string;
  setInput: (input: string) => void;
  onPhraseSelected: (phraseValue?: PhraseValue) => void;
  onPressEnter: (phraseValue?: PhraseValue) => void;
  onFillPhraseText: (data: string) => void;
  onContentClick?: () => void;
}

interface MatchedPhraseListProps {
  mercadoQAmode?: boolean;
  list: PhraseValue[];
  region: General.UpperCaseRegion;
  selectIndex: number;
  onPhraseSelected: (phraseValue?: PhraseValue) => void;
  onPressEnter: (phraseValue?: PhraseValue) => void;
  setSelectIndex: (index: number) => void;
  setInput: (val: string) => void;
  onFillPhraseText: (data: string) => void;
}

function MatchedPhraseList(props: MatchedPhraseListProps) {
  const {
    mercadoQAmode,
    list,
    region,
    selectIndex,
    onPressEnter,
    onPhraseSelected,
    setSelectIndex,
    setInput,
    onFillPhraseText,
  } = props;
  const { opened } = usePhraseTranslateOpened();
  const { t } = useTranslate();

  const itemsRef = useRef<Array<PhraseItemRef | null>>([]);

  const handleSendClick = useMemoizedFn((data: PhraseValue) => {
    onPressEnter?.(data);
    setSelectIndex(-1);
    setInput('');
  });

  const handleEditClick = useMemoizedFn((data: PhrasesTextElement) => {
    setSelectIndex(-1);
    onFillPhraseText?.(getFillPhraseText(data, opened));
  });

  const scrollTo = useMemoizedFn((index: number) => {
    if (itemsRef.current[index]) {
      itemsRef.current[index]?.scrollTo();
    }
  });

  useUnmount(() => {
    onPhraseSelected?.(undefined);
  });

  const content = useMemo(() => {
    return list.map((matched, i) => (
      <PhraseItem
        ref={(el) => {
          itemsRef.current[i] = el;
        }}
        active={i === selectIndex}
        width={520}
        data={matched}
        mercadoQAmode={mercadoQAmode}
        region={region}
        hideTag={false}
        hideTranslateContent={!opened}
        onSendClick={handleSendClick}
        onEditClick={handleEditClick}
        onMouseMove={() => {
          if (selectIndex === i) return;
          setSelectIndex(i);
          onPhraseSelected?.(list[i]);
        }}
        onMouseLeave={() => {
          setSelectIndex(-1);
          onPhraseSelected?.(undefined);
        }}
      />
    ));
  }, [
    list,
    mercadoQAmode,
    selectIndex,
    region,
    opened,
    handleSendClick,
    setSelectIndex,
    onPhraseSelected,
    handleEditClick,
  ]);

  useKeyboardHandler(
    ['ArrowDown', 'ArrowUp', 'Enter', 'Space'],
    (e) => {
      if (e.code === 'ArrowDown') {
        let newVal = selectIndex + 1;
        newVal = newVal < list.length ? newVal : list.length - 1;
        setSelectIndex(newVal);
        scrollTo(newVal);
        onPhraseSelected?.(list[newVal]);
        return false;
      }
      if (e.code === 'ArrowUp') {
        let newVal = selectIndex - 1;
        newVal = newVal >= 0 ? newVal : 0;
        setSelectIndex(newVal);
        scrollTo(newVal);
        onPhraseSelected?.(list[newVal]);
        return false;
      }
      if (e.code === 'Enter') {
        if (list?.[selectIndex]) {
          setInput('');
          onPressEnter?.(list[selectIndex]);
        }
        return false;
      }
      if (e.code === 'Space') {
        if (list?.[selectIndex] && !mercadoQAmode) {
          const { text, onlyText } = getPhraseTextElement(list[selectIndex]);
          if (!onlyText) {
            message.warning(t('scene.chatWorkspace.quickReply.hint.editNotSupported'));
            return;
          }

          if (text) {
            setSelectIndex(-1);
            onFillPhraseText(getFillPhraseText(text, opened));
          }
        }
        return false;
      }

      return true;
    },
    {
      eventType: 'keydown',
      priority: KEYBOARD_PRIORITY.TOOLBAR,
      preventDefault: true,
    },
  );

  return (
    <PopoverContent>
      <PopoverContentHeader>
        <PhraseToolbar mercadoQAmode={mercadoQAmode} />
      </PopoverContentHeader>
      <PopoverItems>{content}</PopoverItems>
    </PopoverContent>
  );
}

function Implement(props: ImplementProps) {
  const {
    phraseList,
    region,
    visible: visibleProp = true,
    mercadoQAmode,
    conversationToggle = true,
    input,
    setInput,
    onPhraseSelected,
    onPressEnter,
    onFillPhraseText,
    onContentClick,
    ...otherProps
  } = props;

  const [_visible, setVisible] = useState(false);
  const visible = _visible && visibleProp;
  const [selectIndex, setSelectIndex] = useState(-1);

  const [matchedPhraseList, setMatchedPhraseList] = useState<PhraseValue[]>([]);

  const searchPhrase = useSearchPhraseV2();
  const setConversationToggle = useSetConversationToggle('passivePhraseHide');

  useEffect(() => {
    if (conversationToggle) {
      setConversationToggle(visible ? false : true);
    }
  }, [visible, conversationToggle, setConversationToggle]);

  useDebounceEffect(
    () => {
      if (!input || !input.trim()) {
        setVisible(false);
        return;
      }

      const matchedPhraseList = sortPhraseList(searchPhrase(input, phraseList, false))?.slice(
        0,
        countLimit,
      );

      setMatchedPhraseList(matchedPhraseList || []);

      const visibleValue = !!(matchedPhraseList && matchedPhraseList.length > 0);
      setVisible(visibleValue);
      if (visibleValue) {
        setSelectIndex(-1);
      }
    },
    [input, phraseList],
    { wait: 500 },
  );

  return (
    <Popover
      overlayClassName={popoverCls}
      visible={visible}
      content={
        visible ? (
          <div onClick={onContentClick}>
            <MatchedPhraseList
              list={matchedPhraseList}
              region={region}
              mercadoQAmode={mercadoQAmode}
              selectIndex={selectIndex}
              onPhraseSelected={onPhraseSelected}
              onPressEnter={onPressEnter}
              setInput={setInput}
              setSelectIndex={setSelectIndex}
              onFillPhraseText={onFillPhraseText}
            />
          </div>
        ) : (
          <></>
        )
      }
      placement="topLeft"
      arrowContent={<div />}
      {...otherProps}
    />
  );
}

export default PassivePopover;
